import {
  Add as AddIcon,
  Delete,
  Edit,
  Image as ImageIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

const API_BASE_URL = process.env.REACT_APP_API_URL;

const validateField = (name, value, formData, imageFile) => {
  switch (name) {
    case "name":
      if (!value || !value.trim()) return "Product name is required";
      if (value.length < 2) return "Product name must be at least 2 characters";
      if (value.length > 100)
        return "Product name cannot exceed 100 characters";
      return "";
    case "model":
      if (!value || !value.trim()) return "Brand & model is required";
      if (value.length < 2)
        return "Brand & model must be at least 2 characters";
      if (value.length > 100)
        return "Brand & model cannot exceed 100 characters";
      return "";
    case "description":
      if (!value || !value.trim()) return "Description is required";
      if (value.length < 10)
        return "Description must be at least 10 characters";
      if (value.length > 1000)
        return "Description cannot exceed 1000 characters";
      return "";
    case "category":
      if (!value) return "Category is required";
      const validCategories = ["Mobile", "Tablet", "Laptop", "Smartwatch"];
      if (!validCategories.includes(value)) return "Invalid category selected";
      return "";
    case "price":
      if (!value && value !== 0) return "Price is required";
      const priceNum = Number(value);
      if (isNaN(priceNum)) return "Price must be a valid number";
      if (priceNum < 2) return "Price must be at least ₹2";
      if (priceNum > 10000) return "Price cannot exceed ₹10,000";
      if (
        formData.discountPrice &&
        Number(formData.discountPrice) >= priceNum
      ) {
        return "Price must be greater than discount price";
      }
      return "";
    case "discountPrice":
      if (value) {
        const discountNum = Number(value);
        if (isNaN(discountNum)) return "Discount price must be a valid number";
        if (discountNum < 1) return "Discount price must be at least ₹1";
        if (discountNum > 10000) return "Discount price cannot exceed ₹10,000";
        if (formData.price && discountNum >= Number(formData.price)) {
          return "Discount price must be less than regular price";
        }
      }
      return "";
    case "image":
      if (!imageFile) return "Product image is required";
      return "";
    default:
      return "";
  }
};

const ProductListPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [formLoading, setFormLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    model: "",
    description: "",
    price: "",
    discountPrice: "",
    category: "",
    inStock: true,
    rating: 4.5,
    reviews: 0,
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    applySearchFilter();
  }, [applySearchFilter]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/products`);
      const fetchedProducts = response.data || [];
      setProducts(fetchedProducts);
      setFilteredProducts(fetchedProducts);
      toast.success("Products loaded successfully");
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const applySearchFilter = useCallback(() => {
    if (!searchTerm.trim()) {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(
        (product) =>
          product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.model.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(filtered);
    }
  }, [searchTerm, products]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleOpenDialog = (product = null) => {
    setEditProduct(product);
    if (product) {
      setFormData({
        name: product.name || "",
        model: product.model || "",
        description: product.description || "",
        price: product.price ? product.price.toString() : "",
        discountPrice: product.discountPrice
          ? product.discountPrice.toString()
          : "",
        category: product.category || "",
        inStock: product.inStock !== undefined ? product.inStock : true,
        rating: product.rating || 4.5,
        reviews: product.reviews || 0,
      });
      setImagePreview(
        product.image
          ? `${"https://covercraft-x28n.onrender.com"}${product.image}`
          : null
      );
    } else {
      setFormData({
        name: "",
        model: "",
        description: "",
        price: "",
        discountPrice: "",
        category: "",
        inStock: true,
        rating: 4.5,
        reviews: 0,
      });
      setImagePreview(null);
    }
    setImageFile(null);
    setFormErrors({});
    requestAnimationFrame(() => setOpenDialog(true));
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditProduct(null);
    setImageFile(null);
    setImagePreview(null);
    setFormErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "image") {
      const file = e.target.files[0];
      if (file) {
        const validTypes = ["image/jpeg", "image/png", "image/jpg"];
        if (!validTypes.includes(file.type)) {
          toast.error("Please upload a valid image (JPEG, PNG, JPG)");
          return;
        }
        if (file.size > 5 * 1024 * 1024) {
          toast.error("Image size must be less than 5MB");
          return;
        }
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
        setFormErrors((prev) => ({ ...prev, image: "" }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      const error = validateField(name, value, formData, imageFile);
      setFormErrors((prev) => ({ ...prev, [name]: error }));

      if (error) {
        toast.error(error);
      }
    }
  };

  const handleStockToggle = (e) => {
    setFormData((prev) => ({
      ...prev,
      inStock: e.target.checked,
    }));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      model: "",
      description: "",
      price: "",
      discountPrice: "",
      category: "",
      inStock: true,
      rating: 4.5,
      reviews: 0,
    });
    setImageFile(null);
    setImagePreview(null);
    setFormErrors({});
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();

    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      newErrors[key] = validateField(key, formData[key], formData, imageFile);
    });
    newErrors.image = editProduct
      ? ""
      : validateField("image", "", formData, imageFile);

    setFormErrors(newErrors);

    const errorMessages = Object.values(newErrors).filter((error) => error);
    if (errorMessages.length > 0) {
      toast.error(errorMessages[0]);
      return;
    }

    setFormLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name.trim());
      formDataToSend.append("model", formData.model.trim());
      formDataToSend.append("description", formData.description.trim());
      formDataToSend.append("price", formData.price);
      formDataToSend.append("discountPrice", formData.discountPrice || "");
      formDataToSend.append("category", formData.category);
      formDataToSend.append("inStock", formData.inStock.toString());
      formDataToSend.append("rating", formData.rating.toString());
      formDataToSend.append("reviews", formData.reviews.toString());
      if (imageFile) formDataToSend.append("image", imageFile);

      let response;
      if (editProduct) {
        response = await fetch(`${API_BASE_URL}/products/${editProduct._id}`, {
          method: "PUT",
          body: formDataToSend,
        });
      } else {
        response = await fetch(`${API_BASE_URL}/add-products`, {
          method: "POST",
          body: formDataToSend,
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            `Failed to ${editProduct ? "update" : "add"} product`
        );
      }

      const productData = await response.json();
      const savedProduct = productData.product || productData;

      setProducts((prev) =>
        editProduct
          ? prev.map((p) => (p._id === editProduct._id ? savedProduct : p))
          : [...prev, savedProduct]
      );

      toast.success(
        `Product ${editProduct ? "updated" : "added"} successfully!`
      );
      handleCloseDialog();
      fetchProducts();
    } catch (error) {
      console.error("Error saving product:", error);
      toast.error(error.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?"))
      return;
    try {
      await axios.delete(`${API_BASE_URL}/products/${productId}`);
      setProducts((prev) => prev.filter((p) => p._id !== productId));
      toast.success("Product deleted successfully");
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error("Failed to delete product");
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 2,
          background: theme.palette.background.paper,
        }}
      >
        <Box sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
          <Typography
            variant={isMobile ? "h5" : "h4"}
            component="h1"
            gutterBottom
            sx={{
              fontWeight: "bold",
              textAlign: "center",
              color: theme.palette.primary.main,
            }}
          >
            Product List
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: "center",
              gap: { xs: 2, sm: 0 },
              mb: 2,
            }}
          >
            <TextField
              variant="outlined"
              placeholder="Search by name or model..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: "100%", sm: "300px", md: "400px" },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "20px",
                  backgroundColor: "white",
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  "&:hover fieldset": { borderColor: "primary.main" },
                  "&.Mui-focused fieldset": { borderColor: "primary.main" },
                },
              }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                height: { xs: "40px", sm: "auto" },
                px: { xs: 2, sm: 3 },
              }}
            >
              Add Product
            </Button>
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Price / Discount Price</TableCell>
                {!isMobile && <TableCell>Image</TableCell>}
                <TableCell>Stock Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isMobile ? 5 : 6} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredProducts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isMobile ? 5 : 6} align="center">
                    No products found
                  </TableCell>
                </TableRow>
              ) : (
                filteredProducts.map((product) => (
                  <TableRow key={product._id} hover>
                    <TableCell>{product.name}</TableCell>
                    <TableCell>{product.model}</TableCell>
                    <TableCell>
                      ₹{product.price}{" "}
                      {product.discountPrice
                        ? `/ ₹${product.discountPrice}`
                        : ""}
                    </TableCell>
                    {!isMobile && (
                      <TableCell>
                        <img
                          src={`${"https://covercraft-x28n.onrender.com"}${
                            product.image
                          }`}
                          alt={product.name}
                          style={{
                            width: 50,
                            height: 50,
                            objectFit: "cover",
                            borderRadius: 4,
                          }}
                          onError={(e) =>
                            (e.target.src = "/fallback-image.jpg")
                          }
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      {product.inStock ? "In Stock" : "Out of Stock"}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenDialog(product)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteProduct(product._id)}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {editProduct ? "Edit Product" : "Add Product"}
        </DialogTitle>
        <DialogContent sx={{ maxHeight: "80vh", overflowY: "auto" }}>
          <Box sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
            <form onSubmit={handleSaveProduct}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, fontSize: { xs: "1rem", sm: "1.25rem" } }}
                  >
                    Basic Information
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Product Name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    size={isMobile ? "small" : "medium"}
                    error={!!formErrors.name}
                    helperText={formErrors.name}
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Brand & Model"
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                    required
                    size={isMobile ? "small" : "medium"}
                    error={!!formErrors.model}
                    helperText={formErrors.model}
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Price"
                    name="price"
                    type="number"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                    error={!!formErrors.price}
                    helperText={formErrors.price}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">₹</InputAdornment>
                      ),
                    }}
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: 2, max: 10000, step: "0.01" }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Discount Price"
                    name="discountPrice"
                    type="number"
                    value={formData.discountPrice}
                    onChange={handleInputChange}
                    error={!!formErrors.discountPrice}
                    helperText={formErrors.discountPrice}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">₹</InputAdornment>
                      ),
                    }}
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: 1, max: 10000, step: "0.01" }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl
                    fullWidth
                    required
                    size={isMobile ? "small" : "medium"}
                    error={!!formErrors.category}
                  >
                    <InputLabel
                      sx={{
                        color: "#000",
                        backgroundColor: formData.category
                          ? "#fff"
                          : "transparent",
                        padding: "0 4px",
                        "&.Mui-focused": {
                          color: "#6C63FF",
                          backgroundColor: "#fff",
                        },
                      }}
                    >
                      Category
                    </InputLabel>
                    <Select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      sx={{
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid #ccc",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid #999",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          border: "2px solid #6C63FF",
                        },
                        backgroundColor: "#fff",
                      }}
                    >
                      <MenuItem value="Mobile">📱 Mobile</MenuItem>
                      <MenuItem value="Tablet">📟 Tablet</MenuItem>
                      <MenuItem value="Laptop">💻 Laptop</MenuItem>
                      <MenuItem value="Smartwatch">⌚ Smartwatch</MenuItem>
                    </Select>
                    {formErrors.category && (
                      <Typography color="error" variant="caption">
                        {formErrors.category}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Button
                    variant="contained"
                    component="label"
                    fullWidth
                    startIcon={<ImageIcon />}
                    sx={{ height: isMobile ? "40px" : "56px" }}
                  >
                    Upload Image
                    <input
                      type="file"
                      hidden
                      accept="image/jpeg,image/png,image/jpg"
                      onChange={handleInputChange}
                      name="image"
                    />
                  </Button>
                  {imagePreview ? (
                    <Box
                      component="img"
                      src={imagePreview}
                      alt="Preview"
                      sx={{
                        mt: 2,
                        maxWidth: "100%",
                        maxHeight: "200px",
                        objectFit: "contain",
                      }}
                      onError={(e) => (e.target.src = "/fallback-image.jpg")}
                    />
                  ) : (
                    <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                      {formErrors.image ||
                        (editProduct ? "Existing image" : "Image is required")}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    multiline
                    rows={4}
                    size={isMobile ? "small" : "medium"}
                    error={!!formErrors.description}
                    helperText={formErrors.description}
                    inputProps={{ maxLength: 1000 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.inStock}
                        onChange={handleStockToggle}
                        color="primary"
                      />
                    }
                    label="In Stock"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexDirection: isMobile ? "column" : "row",
                      justifyContent: "flex-end",
                      mt: 2,
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={resetForm}
                      disabled={formLoading}
                      fullWidth={isMobile}
                      sx={{ minWidth: { sm: "120px" } }}
                    >
                      Reset
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={
                        formLoading ||
                        Object.values(formErrors).some((error) => error)
                      }
                      fullWidth={isMobile}
                      sx={{ minWidth: { sm: "120px" } }}
                      startIcon={
                        formLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <AddIcon />
                        )
                      }
                    >
                      {formLoading
                        ? editProduct
                          ? "Updating..."
                          : "Adding..."
                        : editProduct
                        ? "Update Product"
                        : "Add Product"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Box>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default ProductListPage;
